/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useAuthUserSync } from '@/lib/use-auth-user-sync'
import type { TopNavLink } from '../types'
import { Footer } from './footer'
import { PublicHeader, type PublicHeaderProps } from './public-header'

type PublicLayoutProps = {
  children: React.ReactNode
  showMainContainer?: boolean
  /**
   * Controls whether the canonical site footer is rendered at the bottom of
   * this public page.  Defaults to true.
   *
   * WHY THIS EXISTS — duplicate-footer fix:
   *   PublicLayout is now the single source of truth for <Footer />.
   *   Any page that previously rendered <Footer /> itself (e.g. the home page)
   *   MUST remove that explicit render, otherwise two footers appear.
   *   Pass showFooter={false} only for pages that manage their own footer or
   *   use a full-viewport iframe (custom-content / About-URL mode).
   */
  showFooter?: boolean
  navContent?: React.ReactNode
  headerProps?: Omit<PublicHeaderProps, 'navContent'>
  navLinks?: TopNavLink[]
  showThemeSwitch?: boolean
  showAuthButtons?: boolean
  showNotifications?: boolean
  logo?: React.ReactNode
  siteName?: string
}

export function PublicLayout(props: PublicLayoutProps) {
  const showFooter = props.showFooter !== false

  // The public header shows the signed-in user's balance from the persisted
  // auth.user snapshot; keep it synced with /api/user/self so the homepage
  // balance matches the console/wallet pages after topups.
  useAuthUserSync()

  return (
    <div className='bg-background text-foreground relative min-h-svh overflow-x-clip'>
      <PublicHeader
        navContent={props.navContent}
        navLinks={props.navLinks}
        showThemeSwitch={props.showThemeSwitch}
        showAuthButtons={props.showAuthButtons}
        showNotifications={props.showNotifications}
        logo={props.logo}
        siteName={props.siteName}
        {...props.headerProps}
      />

      {props.showMainContainer !== false ? (
        <main className='container px-4 py-6 pt-20 md:px-4'>
          {props.children}
        </main>
      ) : (
        /* 无容器分支也要有 <main> 地标(a11y: document 缺 main landmark);
           不带类名的 main 是普通块级元素,布局与裸 children 一致 */
        <main>{props.children}</main>
      )}

      {showFooter && <Footer />}
    </div>
  )
}
